import React, { useEffect, useState } from 'react';
import {
  BookOpen,
  ScrollText,
  Shield,
  CreditCard,
  Truck,
  RefreshCw,
  Lock,
  Globe,
  Gavel
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import MainNavbar from '../../components/layout/MainNavbar';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const sections = [
    {
      icon: ScrollText,
      title: 'Acceptance of Terms',
      description: [
        'By accessing or using this website, mobile application, or any related services (collectively, the “Services”), you agree to comply with these Terms & Conditions.',
        'If you do not agree with any part of these Terms, you must discontinue the use of the Services immediately.'
      ],
    },
    {
      icon: BookOpen,
      title: 'Eligibility & Accounts',
      description: [
        'You must be at least the age of majority in your jurisdiction to create an account and use the Services.',
        'You are fully responsible for maintaining the confidentiality of your login credentials and for all activities conducted under your account.'
      ],
    },
    {
      icon: CreditCard,
      title: 'Orders, Pricing & Payments',
      description: [
        'Product prices are displayed in INR unless expressly stated otherwise.',
        'We reserve the right to refuse or cancel any order that appears to be fraudulent or erroneous.',
        'Supported payment methods include UPI, credit/debit cards, net banking, and digital wallets (subject to availability and configuration).'
      ],
    },
    {
      icon: Truck,
      title: 'Shipping & Delivery',
      description: [
        'Estimated delivery timelines are provided during checkout and serve as guidelines rather than guarantees.',
        'Risk of loss and ownership for items transfer to you upon delivery at the address you provide.'
      ],
    },
    {
      icon: RefreshCw,
      title: 'Returns & Refunds',
      description: [
        'You may initiate a return within 7 days of delivery unless a different period is specified on the product page.',
        'Refunds are processed to the original payment method after inspection and approval of the returned items.'
      ],
    },
    {
      icon: Shield,
      title: 'Permitted Use',
      description: [
        'You agree not to misuse the Services, including attempting to reverse engineer the platform, interfering with security features, or infringing upon the rights of others.',
        'Any unauthorized use of the Services may result in termination of your access.'
      ],
    },
    {
      icon: Lock,
      title: 'Intellectual Property',
      description: [
        'All content, trademarks, graphics, and logos displayed on the Services remain the property of their respective owners.',
        'You may not reproduce, distribute, or reuse any content without prior written permission.'
      ],
    },
    {
      icon: Globe,
      title: 'Privacy',
      description: [
        'Your use of the Services is also governed by our Privacy Policy, which explains how we collect, process, and safeguard your personal information.',
        'By using the Services, you consent to the data practices described in the Privacy Policy.'
      ],
    },
    {
      icon: Shield,
      title: 'Disclaimers & Liability',
      description: [
        'The Services are provided on an “as is” and “as available” basis without warranties of any kind, either express or implied.',
        'To the maximum extent permitted by law, we disclaim liability for indirect, incidental, or consequential damages arising from your use of the Services.'
      ],
    },
    {
      icon: Gavel,
      title: 'Governing Law & Disputes',
      description: [
        'These Terms are governed by the laws of Your State, India, without regard to conflict of law principles.',
        'Courts located in Your City shall have exclusive jurisdiction over any disputes, though alternative dispute resolution such as mediation or arbitration may be utilized where applicable.'
      ],
    },
  ];

  return (
    <>
      <div
        className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] text-slate-100'
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Terms & Conditions" />

        {/* Background accents reminiscent of home page */}
        <div className="absolute inset-0 pointer-events-none">
          {theme === 'dark' ? (
            <>
              <div className="absolute -top-24 -left-24 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/3 right-10 w-72 h-72 bg-indigo-500/30 rounded-full blur-[140px] animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-[160px] animate-pulse delay-500" />
            </>
          ) : (
            <>
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
            </>
          )}
        </div>

        <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${theme === 'dark' ? 'bg-slate-900 border-1 border-muted-foreground/20 text-slate-100' : 'bg-white border-1 border-muted-foreground/20 text-slate-700'}`}>
                <ScrollText className="h-4 w-4" />
                Terms & Conditions
              </div>
              <h1 className="text-sm sm:text-3xl font-bold">TrackYourWheelz Terms & Conditions</h1>
              <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                These terms outline the rules that govern the TrackYourWheelz platform. Please review them carefully to understand your rights and responsibilities.
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                Last updated: <span className="font-semibold">November 2025</span>
              </p>
            </header>

            <section className={`rounded-xl border-1 border-muted-foreground/20 ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'} p-6 space-y-3`}>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" />
                Quick Overview
              </h2>
              <ul className={`list-disc ps-5 space-y-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                <li>These terms apply to all use of the TrackYourWheelz platform, mobile app, and services.</li>
                <li>By accessing the platform you agree to follow these rules.</li>
                <li>Key topics include account responsibility, pricing, shipping, returns, privacy, and dispute resolution.</li>
              </ul>
            </section>

            <section className="space-y-6">
              {sections.map((section) => (
                <Card
                  key={section.title}
                  className={`border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'}`}
                >
                  <CardHeader className="flex flex-row items-start gap-3 p-6 pb-3">
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                      <section.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold">{section.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className={`p-6 pt-0 space-y-3 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                    {section.description.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-relaxed">{paragraph}</p>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </section>

            <footer className={`rounded-xl border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'} p-6 space-y-4`}>
              <h2 className="text-lg font-semibold">Need clarification?</h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                Our team can help with questions about these rules. Contact support or return to the homepage for more information.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/login')} className="w-full sm:w-48" variant='outline'>Contact Support</Button>
                <Button variant="outline" onClick={() => navigate('/home')} className="w-full sm:w-48">Back to Home</Button>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default TermsAndConditions;